import { motion } from 'framer-motion';
import { Seo } from '@/components/Seo';

export const Privacy = () => {
    return (
        <div data-testid="privacy-page">
            <Seo
                title="Privacy Policy | CatchingOn"
                description="How CatchingOn collects, uses and protects your personal data."
                path="/privacy"
            />
            <section className="pt-32 pb-20 bg-brand-periwinkle min-h-screen px-6 md:px-12">
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="text-sm font-bold tracking-widest text-black/60 mb-6">LEGAL</p>
                        <h1 className="text-4xl sm:text-5xl font-black text-black leading-tight mb-6">
                            Privacy Policy
                        </h1>
                        <p className="text-black/60 mb-12">
                            Last updated: 14 March 2026
                        </p>

                        <div className="space-y-10 text-black/80">
                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Who We Are</h2>
                                <p>
                                    CatchingOn is a freelance marketing business operated by Ellie.
                                    This website is built and hosted by Dockside Design.
                                    You can contact us at{' '}
                                    <a href="mailto:ellie@catchingon.co.uk" className="text-black font-bold underline hover:text-brand-lime transition-colors">
                                        ellie@catchingon.co.uk
                                    </a>.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">What Data We Collect</h2>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                        <strong className="text-black">Contact form submissions:</strong> Your name, email address,
                                        and message content.
                                    </li>
                                    <li>
                                        <strong className="text-black">Email subscriptions:</strong> Your email address when you sign up for updates.
                                    </li>
                                    <li>
                                        <strong className="text-black">Analytics cookies:</strong> If you accept cookies, Google Analytics 4
                                        collects anonymised usage data (pages visited, session duration, device type).
                                        No personally identifiable information is collected through analytics.
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Why We Collect It (Lawful Basis)</h2>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                        <strong className="text-black">Contact forms:</strong> Legitimate interest — to respond to your
                                        enquiry and provide the service you requested (UK GDPR Article 6(1)(f)).
                                    </li>
                                    <li>
                                        <strong className="text-black">Email subscriptions:</strong> Consent — you actively opt in by
                                        subscribing and confirming via a double opt-in email (UK GDPR Article 6(1)(a)).
                                    </li>
                                    <li>
                                        <strong className="text-black">Analytics cookies:</strong> Consent — cookies are only set after you
                                        click "Accept" on the cookie banner (PECR Regulation 6).
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">How Long We Keep Your Data</h2>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                        <strong className="text-black">Contact form submissions:</strong> Retained for up to 12 months,
                                        then automatically deleted.
                                    </li>
                                    <li>
                                        <strong className="text-black">Email subscriptions:</strong> Retained until you unsubscribe or request deletion.
                                    </li>
                                    <li>
                                        <strong className="text-black">Analytics data:</strong> Google Analytics retains data for 14 months
                                        by default. We do not export or store analytics data separately.
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Who We Share Your Data With</h2>
                                <p className="mb-3">
                                    We do not sell your data. We share data only with the following service providers who process it on our behalf:
                                </p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                        <strong className="text-black">Amazon Web Services (AWS):</strong> Hosts our website and stores
                                        form submissions. Data is processed in the EU (Stockholm, eu-north-1 region).
                                    </li>
                                    <li>
                                        <strong className="text-black">Google Analytics:</strong> Processes anonymised analytics data
                                        (only if you accept cookies). Google may transfer data outside the UK under their standard contractual clauses.
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Cookies</h2>
                                <p className="mb-3">
                                    We use one type of cookie on this site:
                                </p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                        <strong className="text-black">Google Analytics cookies (_ga, _ga_*):</strong> These are only
                                        set if you click "Accept" on the cookie banner. They help us understand how visitors use the site.
                                    </li>
                                </ul>
                                <p className="mt-3">
                                    We also store your cookie preference in your browser's local storage (not a cookie) so we don't ask
                                    you again on each visit. You can clear this at any time via your browser settings.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Your Rights</h2>
                                <p className="mb-3">Under UK GDPR, you have the right to:</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><strong className="text-black">Access</strong> the personal data we hold about you</li>
                                    <li><strong className="text-black">Rectify</strong> any inaccurate data</li>
                                    <li><strong className="text-black">Erase</strong> your data ("right to be forgotten")</li>
                                    <li><strong className="text-black">Withdraw consent</strong> for email subscriptions or analytics cookies at any time</li>
                                    <li><strong className="text-black">Object</strong> to processing based on legitimate interest</li>
                                    <li>
                                        <strong className="text-black">Lodge a complaint</strong> with the Information Commissioner's Office (ICO)
                                        at{' '}
                                        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-black font-bold underline hover:text-brand-lime transition-colors">
                                            ico.org.uk
                                        </a>
                                    </li>
                                </ul>
                                <p className="mt-3">
                                    To exercise any of these rights, email us at{' '}
                                    <a href="mailto:ellie@catchingon.co.uk" className="text-black font-bold underline hover:text-brand-lime transition-colors">
                                        ellie@catchingon.co.uk
                                    </a>.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-black text-black mb-3">Changes to This Policy</h2>
                                <p>
                                    We may update this policy from time to time. Any changes will be posted on this page with an updated date.
                                </p>
                            </section>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Privacy;
